"use client";

import { canAutoReload, markAutoReload } from "@shared/lib/auto-reload";
import { reportClientError } from "@shared/lib/report-client-error";
import { useEffect, useMemo } from "react";

/**
 * Last-resort boundary: it replaces the root layout, so there is no i18n
 * provider, no theme and no guarantee the stylesheet is still applied. Keep it
 * self-contained — inline styles, English copy, zero imports from the design
 * system.
 */

export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	// Decided during render so a page that is about to reload shows a blank
	// screen instead of flashing "Something went wrong" first.
	const reloading = useMemo(() => canAutoReload(error), [error]);

	useEffect(() => {
		if (reloading) {
			markAutoReload();
		}
		// Report first: the beacon survives the reload that follows.
		reportClientError("global-boundary", error, {
			digest: error.digest ?? "",
			reloading,
		});
		if (reloading) {
			window.location.reload();
		}
	}, [error, reloading]);

	if (reloading) {
		return (
			<html lang="en">
				<body style={{ margin: 0, backgroundColor: "#ffffff" }} />
			</html>
		);
	}

	return (
		<html lang="en">
			<body
				style={{
					margin: 0,
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#ffffff",
					color: "#18181b",
					fontFamily:
						"system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
					padding: "24px",
					textAlign: "center",
				}}
			>
				<div style={{ maxWidth: "28rem" }}>
					<h1
						style={{
							fontSize: "1.5rem",
							fontWeight: 700,
							margin: 0,
						}}
					>
						Something went wrong
					</h1>
					<p
						style={{
							marginTop: "0.5rem",
							color: "#71717a",
							lineHeight: 1.6,
						}}
					>
						This page hit an unexpected error. Trying again usually
						fixes it.
					</p>

					<div
						style={{
							marginTop: "1.5rem",
							display: "flex",
							gap: "0.75rem",
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<button
							type="button"
							onClick={reset}
							style={{
								padding: "0.625rem 1.25rem",
								borderRadius: "0.375rem",
								border: "1px solid #6d28d9",
								backgroundColor: "#6d28d9",
								color: "#ffffff",
								fontWeight: 600,
								cursor: "pointer",
							}}
						>
							Try again
						</button>
						<a
							href="/"
							style={{
								padding: "0.625rem 1.25rem",
								borderRadius: "0.375rem",
								border: "1px solid #d4d4d8",
								color: "#18181b",
								fontWeight: 600,
								textDecoration: "none",
							}}
						>
							Back to home
						</a>
					</div>

					{error.digest && (
						<p
							style={{
								marginTop: "1.5rem",
								fontSize: "0.75rem",
								color: "#a1a1aa",
							}}
						>
							Reference: {error.digest}
						</p>
					)}
				</div>
			</body>
		</html>
	);
}
